import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassFormGroup - Form field container with label and error message
 *
 * A wrapper component that provides consistent styling for form fields
 * including label, input, and error/helper text.
 *
 * @example
 * ```tsx
 * <GlassFormGroup
 *   label="Email Address"
 *   error="Invalid email format"
 *   required
 * >
 *   <GlassInput value={email} onChange={setEmail} />
 * </GlassFormGroup>
 * ```
 */

export interface GlassFormGroupProps {
  /** Form field label */
  label?: string;
  /** HTML for attribute (links label to input) */
  htmlFor?: string;
  /** Error message */
  error?: string;
  /** Helper text (shown when no error) */
  helperText?: string;
  /** Required field indicator */
  required?: boolean;
  /** Form field children (input, select, etc.) */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Label placement */
  labelPlacement?: 'top' | 'left';
  /** Label width (for left placement) */
  labelWidth?: string;
}

export const GlassFormGroup: React.FC<GlassFormGroupProps> = ({
  label,
  htmlFor,
  error,
  helperText,
  required,
  children,
  className,
  labelPlacement = 'top',
  labelWidth = '120px',
}) => {
  const isHorizontal = labelPlacement === 'left';

  return (
    <div
      className={cn(
        isHorizontal ? 'flex items-start gap-4' : 'space-y-1.5',
        className
      )}
    >
      {label && (
        <label
          htmlFor={htmlFor}
          className={cn(
            'text-sm font-medium text-muted-foreground',
            'flex items-center gap-1',
            isHorizontal && 'pt-2 flex-shrink-0'
          )}
          style={isHorizontal ? { width: labelWidth } : undefined}
        >
          {label}
          {required && <span className="text-rose-500">*</span>}
        </label>
      )}

      <div className={cn('flex-1', isHorizontal && 'min-w-0')}>
        {children}

        {/* Error message */}
        {error && (
          <p className="mt-1.5 text-sm text-rose-500 animate-in fade-in slide-in-from-top-1 duration-200">
            {error}
          </p>
        )}

        {/* Helper text */}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-muted-foreground/60">{helperText}</p>
        )}
      </div>
    </div>
  );
};

GlassFormGroup.displayName = 'GlassFormGroup';

/**
 * GlassFormRow - Horizontal form field group
 */
export interface GlassFormRowProps {
  /** Row children (form groups) */
  children: React.ReactNode;
  /** Gap between items */
  gap?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

export const GlassFormRow: React.FC<GlassFormRowProps> = ({
  children,
  gap = 'md',
  className,
}) => {
  const gapClasses = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6',
  };

  return (
    <div className={cn('flex flex-wrap', gapClasses[gap], className)}>
      {React.Children.map(children, (child) => (
        <div className="flex-1 min-w-[200px]">{child}</div>
      ))}
    </div>
  );
};

GlassFormRow.displayName = 'GlassFormRow';
